import { formatDate } from "@/lib/utils";
import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  Wrench,
  Filter,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Clock,
  Package,
  CheckCircle,
  Loader2,
  RefreshCw,
  Download,
  CreditCard,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/data-table";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { all_routes } from "../../Router/all_routes";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { PageContainer } from "@/components/layout/PageContainer";
import RepairDetailsModal from "./components/RepairDetailsModal";
import FilterRepairModal from "./components/FilterRepairModal";
import { DEFAULT_FILTERS, DEFAULT_WITH } from "./repairConstants";
import AddPartsModal from "./components/AddPartsModal";
import PartsViewModal from "./components/PartsViewModal";
import LaborEntriesModal from "./components/LaborEntriesModal";
import LaborViewModal from "./components/LaborViewModal";
import PDFModal from "./components/PDFModal";
import RecordRepairPaymentModal from "./components/RecordRepairPaymentModal";
import StatusHistoryModal from "./components/StatusHistoryModal";
import StatusUpdateModal from "./components/StatusUpdateModal";
import RecordNotFound from "@/core/components/RecordNotFound";

const isRepairPaymentCompleted = (repair) => {
  if (!repair) return false;
  const pStatus = (repair.payment_status || "").toLowerCase();
  if (pStatus === "completed" || pStatus === "paid") return true;
  const balanceDue =
    repair.balance_due != null ? parseFloat(repair.balance_due) : null;
  const paidAmount = parseFloat(repair.paid_amount || 0);
  const totalCost = parseFloat(repair.total_cost ?? repair.estimated_cost ?? 0);
  if (balanceDue !== null && balanceDue <= 0 && (paidAmount > 0 || totalCost > 0)) {
    return true;
  }
  return false;
};

const RepairDashboard = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const currentStore = useSelector((state) => state.current_store);
  const currentStoreId = currentStore?.id;
  const currency = currentStore?.default_currency;
  const currencySymbol = currency?.symbol || "";

  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const searchDebounceRef = useRef(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    store_id: currentStoreId ? currentStoreId.toString() : "",
  }));
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const initialStoreSetRef = useRef(Boolean(currentStoreId));
  const prevStoreIdRef = useRef(currentStoreId);

  // Sync current store into filters when available, but allow user to remove it
  useEffect(() => {
    if (currentStoreId) {
      if (!initialStoreSetRef.current) {
        initialStoreSetRef.current = true;
        prevStoreIdRef.current = currentStoreId;
        setFilters((prev) => ({
          ...prev,
          store_id: currentStoreId.toString(),
        }));
      } else if (
        prevStoreIdRef.current &&
        prevStoreIdRef.current !== currentStoreId
      ) {
        // Active store in Redux was changed to a different store
        prevStoreIdRef.current = currentStoreId;
        setFilters((prev) => ({
          ...prev,
          store_id: currentStoreId.toString(),
        }));
      }
    }
  }, [currentStoreId]);

  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);

  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [statusHistoryRepair, setStatusHistoryRepair] = useState(null);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfRepairData, setPdfRepairData] = useState(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentRepair, setPaymentRepair] = useState(null);

  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [statusUpdateRepair, setStatusUpdateRepair] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", notes: "" });

  const [addPartsOpen, setAddPartsOpen] = useState(false);
  const [addPartsRepair, setAddPartsRepair] = useState(null);
  const [partsFormData, setPartsFormData] = useState({
    product_id: "",
    quantity_used: 1,
    unit_cost: 0,
    notes: "",
    serial_numbers: [],
    inventory_batch_id: null,
  });
  const [partsViewOpen, setPartsViewOpen] = useState(false);
  const [partsViewRepair, setPartsViewRepair] = useState(null);
  const [productSearchTerm] = useState("");

  const [laborModalOpen, setLaborModalOpen] = useState(false);
  const [laborViewOpen, setLaborViewOpen] = useState(false);
  const [laborRepair, setLaborRepair] = useState(null);
  const [newLaborEntry, setNewLaborEntry] = useState({
    technician_id: "",
    hourly_rate: "",
    start_time: "",
    end_time: "",
    description: "",
  });

  // Debounce search input - 500ms before firing query
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchText(searchInput);
    }, 500);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  // Reset pagination when debounced search or filters change
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchText, filters]);



  // Count active filters for badge
  const activeFilterCount = [
    filters.priority,
    filters.status,
    filters.store_id,
    filters.technician_id,
    filters.date_from,
    filters.date_to,
    ...(filters.repair_category_ids || []),
    filters.sort_by !== "created_at" || filters.sort_direction !== "desc"
      ? filters.sort_by
      : null,
    filters.with && filters.with.length !== DEFAULT_WITH.length ? "with" : null,
  ].filter(Boolean).length;

  const removeFilter = (key, value = null) => {
    setFilters((prev) => {
      if (key === "repair_category_ids" && value !== null) {
        return {
          ...prev,
          repair_category_ids: (prev.repair_category_ids || []).filter(
            (id) => Number(id) !== Number(value),
          ),
        };
      }
      if (key === "sort") {
        return {
          ...prev,
          sort_by: "created_at",
          sort_direction: "desc",
        };
      }
      if (key === "with") {
        return {
          ...prev,
          with: DEFAULT_WITH,
        };
      }
      if (key === "store_id") {
        return {
          ...prev,
          store_id: "",
        };
      }
      return {
        ...prev,
        [key]: Array.isArray(prev[key]) ? [] : "",
      };
    });
  };

  const resetAllFilters = () => {
    setFilters({
      ...DEFAULT_FILTERS,
      store_id: "",
    });
    setSearchInput("");
    setSearchText("");
  };

  const handleSort = (field) => {
    setFilters((prev) => {
      if (prev.sort_by === field) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        ...prev,
        sort_by: field,
        sort_direction: "desc",
      };
    });
  };

  const renderSortableHeader = (field, label) => {
    const isSorted = filters.sort_by === field;
    const isAsc = filters.sort_direction === "asc";
    return (
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 text-xs font-semibold hover:bg-muted/50 data-[state=open]:bg-accent"
        onClick={() => handleSort(field)}
      >
        <span>{label}</span>
        {isSorted ? (
          isAsc ? (
            <ArrowUp className="ml-1.5 h-3.5 w-3.5 text-primary" />
          ) : (
            <ArrowDown className="ml-1.5 h-3.5 w-3.5 text-primary" />
          )
        ) : (
          <ArrowUpDown className="ml-1.5 h-3.5 w-3.5 text-muted-foreground/40" />
        )}
      </Button>
    );
  };

  // ==================== QUERIES ====================
  const { data: repairData, isLoading: isRepairJobsLoading } = useQuery({
    queryKey: [
      "repair-jobs",
      pagination.pageIndex,
      pagination.pageSize,
      searchText,
      filters,
    ],
    queryFn: async () => {
      const perPage = Math.min(
        100,
        Math.max(1, parseInt(pagination.pageSize, 10) || 10),
      );

      const params = {
        page: pagination.pageIndex + 1,
        per_page: perPage,
        sort_by: filters.sort_by || "created_at",
        sort_direction: filters.sort_direction || "desc",
      };

      // with parameter (array of allowed relations)
      if (Array.isArray(filters.with) && filters.with.length > 0) {
        params.with = filters.with;
      } else {
        params.with = DEFAULT_WITH;
      }

      // search (<= 255 chars)
      if (searchText && searchText.trim()) {
        params.search = searchText.trim().slice(0, 255);
      }

      // status
      if (
        filters.status &&
        [
          "pending",
          "in_progress",
          "awaiting_parts",
          "completed",
          "cancelled",
        ].includes(filters.status)
      ) {
        params.status = filters.status;
      }

      // priority
      if (
        filters.priority &&
        ["low", "normal", "high", "urgent"].includes(filters.priority)
      ) {
        params.priority = filters.priority;
      }

      // technician_id (uuid)
      if (filters.technician_id && filters.technician_id !== "all") {
        params.technician_id = filters.technician_id;
      }

      // store_id (uuid)
      if (filters.store_id && filters.store_id !== "all") {
        params.store_id = filters.store_id;
      }

      // date_from (string<date-time>)
      if (filters.date_from) {
        const dFrom = new Date(filters.date_from);
        if (!isNaN(dFrom.getTime())) {
          params.date_from = dFrom.toISOString();
        }
      }

      // date_to (string<date-time>)
      if (filters.date_to) {
        const dTo = new Date(filters.date_to);
        if (!isNaN(dTo.getTime())) {
          params.date_to = dTo.toISOString();
        }
      }

      // repair_category_ids (array[integer])
      if (
        Array.isArray(filters.repair_category_ids) &&
        filters.repair_category_ids.length > 0
      ) {
        const catIds = filters.repair_category_ids
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
        if (catIds.length > 0) {
          params.repair_category_ids = catIds;
        }
      }

      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.JOBS, {
        headers: filters.store_id ? { "X-Store-Id": filters.store_id } : { "X-Store-Id": null },
        params,
      });
      return res.data;
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["repair-categories"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.CATEGORIES);
      return res.data.data || [];
    },
  });

  const { data: technicians = [], isLoading: isTechniciansLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES);
      return (res.data.data || []).filter((e) => e.status === "active");
    },
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
  });

  const { data: repairStats = null, isLoading: isRepairStatsLoading } =
    useQuery({
      queryKey: ["repair-stats"],
      queryFn: async () => {
        const res = await apiClient.get(`/repair-dashboard/stats`);
        return res.data;
      },
      enabled: hasPermission("repair dashboard view"),
    });

  const repairJobs = repairData?.data || [];
  const pageCount = repairData?.meta?.last_page || 1;
  const paginationMeta = {
    total: repairData?.meta?.total || 0,
    from: repairData?.meta?.from || 0,
    to: repairData?.meta?.to || 0,
  };

  // ==================== MUTATIONS ====================
  const deleteRepairMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/repair-jobs/${id}`);
    },
    onSuccess: () => {
      toast.success("Repair job deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["repair-stats"] });
      setDeleteConfirmationId(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete repair job",
      );
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes }) =>
      apiClient.post(API_ENDPOINTS.REPAIRS.STATUS_UPDATE(id), {
        status,
        notes: notes || null,
      }),
    onSuccess: () => {
      toast.success("Status updated successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["repair-stats"] });
      setStatusUpdateOpen(false);
      setStatusUpdateRepair(null);
      setStatusForm({ status: "", notes: "" });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update repair status",
      );
    },
  });

  const { data: products = [], isFetching: isProductsLoading } = useQuery({
    queryKey: ["pos-products-repair", productSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          per_page: 50,
          search: productSearchTerm || undefined,
          with: [
            "brand",
            "model",
            "unit",
            "categories",
            "serial_numbers",
            "batches",
          ],
        },
      });
      return res.data.data || [];
    },
    enabled: addPartsOpen,
  });

  const addPartsMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.post(API_ENDPOINTS.REPAIRS.PARTS(id), data),
    onSuccess: () => {
      toast.success("Part added successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      setAddPartsOpen(false);
      setAddPartsRepair(null);
      setPartsFormData({
        product_id: "",
        quantity_used: 1,
        unit_cost: 0,
        notes: "",
        serial_numbers: [],
        inventory_batch_id: null,
        inventory_batch_number: "",
        tracking_type: "none",
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add part");
    },
  });

  const handleProductSelect = async (e) => {
    const productId = e.target.value;
    const product = products.find((p) => String(p.id) === String(productId));
    const trackingType =
      product?.tracking_type ||
      (product?.is_serialized
        ? "serial"
        : product?.has_batches
          ? "standard"
          : "none");

    setPartsFormData((f) => ({
      ...f,
      product_id: productId,
      unit_cost: parseFloat(product?.sell_price || 0),
      tracking_type: trackingType,
      inventory_batch_id: null,
      inventory_batch_number: "",
      serial_numbers: [],
    }));
    if (productId) {
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(productId),
        );
        const details = res.data.data;
        if (details) {
          const updatedTracking =
            details.tracking_type ||
            (details.is_serialized
              ? "serial"
              : details.has_batches
                ? "standard"
                : trackingType);
          setPartsFormData((f) => ({
            ...f,
            unit_cost: parseFloat(details.sell_price || f.unit_cost || 0),
            tracking_type: updatedTracking,
          }));
        }
      } catch {
        // Handle error gracefully if product fetch fails
      }
    }
  };

  const handleSerialNumbersChange = (value) => {
    const serials = value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setPartsFormData((f) => ({ ...f, serial_numbers: serials }));
  };

  const handleSaveParts = () => {
    if (!partsFormData.product_id) {
      toast.error("Please select a product");
      return;
    }
    const qty = parseFloat(partsFormData.quantity_used);
    if (!qty || qty <= 0) {
      toast.error("Quantity must be greater than 0");
      return;
    }
    const product = products.find(
      (p) => String(p.id) === String(partsFormData.product_id),
    );
    const trackingType =
      partsFormData.tracking_type ||
      product?.tracking_type ||
      (product?.is_serialized
        ? "serial"
        : product?.has_batches
          ? "standard"
          : "none");

    if (trackingType === "standard" && !partsFormData.inventory_batch_id) {
      toast.error("Please select an inventory batch for this product");
      return;
    }
    if (trackingType === "serial") {
      const serialsCount = (partsFormData.serial_numbers || []).length;
      if (serialsCount !== qty) {
        toast.error(
          `Please select exactly ${qty} serial number${qty > 1 ? "s" : ""} (currently selected: ${serialsCount})`,
        );
        return;
      }
    }

    addPartsMutation.mutate({
      id: addPartsRepair.id,
      data: {
        product_id: partsFormData.product_id,
        inventory_batch_id: partsFormData.inventory_batch_id || null,
        quantity_used: qty,
        unit_cost: parseFloat(partsFormData.unit_cost) || 0,
        notes: partsFormData.notes || null,
        serial_numbers:
          trackingType === "serial" && partsFormData.serial_numbers?.length > 0
            ? partsFormData.serial_numbers
            : null,
      },
    });
  };

  const handleViewParts = (repair) => {
    setPartsViewRepair(repair);
    setPartsViewOpen(true);
  };

  const handleAddParts = (repair) => {
    setAddPartsRepair(repair);
    setPartsFormData({
      product_id: "",
      quantity_used: 1,
      unit_cost: 0,
      notes: "",
      serial_numbers: [],
      inventory_batch_id: null,
      inventory_batch_number: "",
      tracking_type: "none",
    });
    setAddPartsOpen(true);
  };

  const addLaborMutation = useMutation({
    mutationFn: ({ id, data }) =>
      apiClient.post(API_ENDPOINTS.REPAIRS.LABOR(id), data),
    onSuccess: () => {
      toast.success("Labor entry added successfully");
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
      setLaborModalOpen(false);
      setLaborRepair(null);
      setNewLaborEntry({
        technician_id: "",
        hourly_rate: "",
        start_time: "",
        end_time: "",
        description: "",
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add labor entry");
    },
  });

  const handleAddLabor = (repair) => {
    setLaborRepair(repair);
    setNewLaborEntry({
      technician_id: "",
      hourly_rate: "",
      start_time: "",
      end_time: "",
      description: "",
    });
    setLaborModalOpen(true);
  };

  const handleViewLabor = (repair) => {
    setLaborRepair(repair);
    setLaborViewOpen(true);
  };

  const handleLaborEntryChange = (e) => {
    const { name, value } = e.target;
    if (name === "technician_id") {
      const tech = technicians.find((t) => t.id.toString() === value);
      setNewLaborEntry((prev) => ({
        ...prev,
        technician_id: value,
        hourly_rate: tech?.hourly_rate ?? prev.hourly_rate,
      }));
    } else {
      setNewLaborEntry((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLaborEntrySave = () => {
    if (
      !newLaborEntry.technician_id ||
      !newLaborEntry.start_time ||
      !newLaborEntry.hourly_rate
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const startDate = new Date(newLaborEntry.start_time);
    if (isNaN(startDate.getTime())) {
      toast.error("Invalid start time");
      return;
    }

    let endDate = null;
    if (newLaborEntry.end_time) {
      endDate = new Date(newLaborEntry.end_time);
      if (isNaN(endDate.getTime())) {
        toast.error("Invalid end time");
        return;
      }
      if (endDate <= startDate) {
        toast.error("End time must be after start time");
        return;
      }
    }

    addLaborMutation.mutate({
      id: laborRepair.id,
      data: {
        technician_id: newLaborEntry.technician_id,
        start_time: startDate.toISOString(),
        end_time: endDate ? endDate.toISOString() : null,
        hourly_rate: parseFloat(newLaborEntry.hourly_rate),
        description: newLaborEntry.description || null,
      },
    });
  };

  // ==================== HELPERS ====================
  const getStatusBadge = (status) => {
    const statusValue = status?.value || status;
    const statusLabel = status?.label || statusValue;

    const statusConfig = {
      completed: { variant: "success" },
      delivered: { variant: "success" },
      ready: { variant: "success" },
      ready_for_pickup: { variant: "success" },
      in_progress: { variant: "info" },
      diagnosing: { variant: "info" },
      testing: { variant: "info" },
      awaiting_parts: { variant: "warning" },
      on_hold: { variant: "warning" },
      cancelled: { variant: "danger" },
      rejected: { variant: "danger" },
      pending: { variant: "warning" },
      new: { variant: "info" },
    };

    const config = statusConfig[statusValue?.toLowerCase()] || {
      variant: "secondary",
    };

    if (!hasPermission("repair dashboard view")) {
      return <RecordNotFound />;
    }

    return (
      <Badge variant={config.variant} className="capitalize">
        {statusLabel?.replace(/_/g, " ")}
      </Badge>
    );
  };

  const handleGeneratePDFReport = async (id, type = "invoice") => {
    try {
      setPdfGenerating(true);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      const endpoint =
        type === "job-note"
          ? "job-note"
          : type === "job-label"
            ? "job-label"
            : "invoice";

      const response = await apiClient.get(
        `${API_BASE_URL}/reports/pdf/repairs/${id}/${endpoint}`,
        { responseType: "blob" },
      );

      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("PDF Report error:", error);
      toast.error("Error", {
        description:
          error.response?.data?.message || "Failed to generate PDF report",
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleClosePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfGenerating(false);
    setPdfModalOpen(false);
    setPdfRepairData(null);
  };

  const getPriorityBadge = (priority) => {
    const priorityValue = priority?.value || priority;
    const priorityLabel = priority?.label || priorityValue;

    const priorityConfig = {
      urgent: { variant: "danger" },
      critical: { variant: "danger" },
      high: { variant: "warning" },
      medium: { variant: "info" },
      normal: { variant: "info" },
      low: { variant: "success" },
    };

    const config = priorityConfig[priorityValue?.toLowerCase()] || {
      variant: "secondary",
    };

    return (
      <Badge variant={config.variant} className="capitalize">
        {priorityLabel}
      </Badge>
    );
  };

  // ==================== STATS CARDS ====================
  const statsCards = [
    {
      title: "Total Repairs",
      value: repairStats?.summary?.total_jobs || 0,
      icon: Wrench,
      color: "bg-blue-500",
    },
    {
      title: "In Progress",
      value: repairStats?.summary?.in_progress_jobs || 0,
      icon: Clock,
      color: "bg-yellow-500",
    },
    {
      title: "Completed",
      value: repairStats?.summary?.completed_jobs || 0,
      icon: CheckCircle,
      color: "bg-green-500",
    },
    {
      title: "Awaiting Parts",
      value: repairStats?.summary?.awaiting_parts_jobs || 0,
      icon: Package,
      color: "bg-orange-500",
    },
  ];

  // ==================== TABLE COLUMNS ====================
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "repair_number",
        header: () => renderSortableHeader("repair_number", "Repair No."),
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div
              className="font-medium cursor-pointer hover:underline text-primary"
              onClick={() => {
                setSelectedRepair(record);
                setViewDetailsModalOpen(true);
              }}
            >
              {record.repair_number}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: () => renderSortableHeader("status", "Status"),
        cell: ({ row }) => getStatusBadge(row.original.status),
      },
      {
        accessorFn: (row) => {
          if (!row.customer) return "-";
          if (
            row.customer.customer_type !== "individual" &&
            row.customer.company
          ) {
            return row.customer.company;
          }
          return row.customer.full_name || row.customer.name || "-";
        },
        header: "Customer",
      },
      {
        accessorFn: (row) => {
          const cats = row.repair_categories || row.repairCategories;
          if (!cats || cats.length === 0) return "-";
          return cats.map((cat) => cat.name).join(", ");
        },
        header: "Category",
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <div className="max-w-[180px] truncate" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        accessorFn: (row) =>
          row.technician?.full_name || row.technician?.name || "-",
        header: "Technician",
      },
      {
        accessorKey: "priority",
        header: () => renderSortableHeader("priority", "Priority"),
        cell: ({ getValue }) => getPriorityBadge(getValue()),
      },
      {
        accessorKey: "total_cost",
        header: () => renderSortableHeader("total_cost", "Cost / Balance"),
        cell: ({ row }) => {
          const record = row.original;
          const total = record.total_cost ?? record.estimated_cost;
          const totalVal = parseFloat(total || 0);
          const balanceDueVal =
            record.balance_due != null ? parseFloat(record.balance_due) : null;
          const paymentStatus = (record.payment_status || "").toLowerCase();
          const isPaid =
            paymentStatus === "completed" || paymentStatus === "paid";
          const effectiveBalanceDue =
            balanceDueVal != null
              ? balanceDueVal
              : !isPaid && totalVal > 0
                ? totalVal
                : 0;

          return (
            <div className="space-y-0.5">
              <span className="font-medium">
                {total != null ? `${currencySymbol}${totalVal.toFixed(2)}` : "-"}
              </span>
              {hasPermission("repair job view_payments") && (
                <>
                  {effectiveBalanceDue > 0 ? (
                    <div className="text-[11px] font-semibold text-amber-600">
                      Due: {currencySymbol}
                      {effectiveBalanceDue.toFixed(2)}
                    </div>
                  ) : isPaid &&
                    (totalVal > 0 || parseFloat(record.paid_amount || 0) > 0) ? (
                    <div className="text-[11px] font-semibold text-emerald-600">
                      Paid in full
                    </div>
                  ) : null}
                </>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "estimated_completion_date",
        header: () =>
          renderSortableHeader(
            "estimated_completion_date",
            "Est. Completion",
          ),
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        accessorKey: "created_at",
        header: () => renderSortableHeader("created_at", "Created"),
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const repair = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("repair job read") && (
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedRepair(repair);
                      setViewDetailsModalOpen(true);
                    }}
                  >
                    <Eye className="mr-2 h-4 w-4" /> View Details
                  </DropdownMenuItem>
                )}
                {hasPermission("repair job update") && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(all_routes.editRepair.replace(":id", repair.id))
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {hasPermission("repair job add_payment") &&
                  !isRepairPaymentCompleted(repair) && (
                    <DropdownMenuItem
                      onClick={() => {
                        setPaymentRepair(repair);
                        setPaymentModalOpen(true);
                      }}
                    >
                      <CreditCard className="mr-2 h-4 w-4 text-emerald-600" /> Record Payment
                    </DropdownMenuItem>
                  )}
                {hasPermission("repair job update_status") && (
                  <DropdownMenuItem
                    onClick={() => {
                      setStatusUpdateRepair(repair);
                      setStatusForm({
                        status:
                          typeof repair.status === "string"
                            ? repair.status
                            : repair.status?.value || "",
                        notes: "",
                      });
                      setStatusUpdateOpen(true);
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> Update Status
                  </DropdownMenuItem>
                )}
                {hasPermission("repair job read") && (
                  <DropdownMenuItem
                    onClick={() => {
                      setStatusHistoryRepair(repair);
                      setStatusHistoryOpen(true);
                    }}
                  >
                    <Clock className="mr-2 h-4 w-4" /> Status History
                  </DropdownMenuItem>
                )}
                {hasPermission("repair job add_part") && (
                  <DropdownMenuItem onClick={() => handleAddParts(repair)}>
                    <Package className="mr-2 h-4 w-4" /> Add Part
                  </DropdownMenuItem>
                )}
                {hasPermission("repair job add_labor") && (
                  <DropdownMenuItem onClick={() => handleAddLabor(repair)}>
                    <Clock className="mr-2 h-4 w-4" /> Add Labor
                  </DropdownMenuItem>
                )}
                {hasPermission("repair job read") && (
                  <DropdownMenuItem onClick={() => handleViewLabor(repair)}>
                    <Eye className="mr-2 h-4 w-4" /> View Labor
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {hasPermission("repair job read") && (
                  <DropdownMenuItem
                    onClick={() => {
                      setPdfRepairData(repair);
                      setPdfModalOpen(true);
                    }}
                  >
                    <Download className="mr-2 h-4 w-4" /> Print PDFs
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {hasPermission("repair job delete") && (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setDeleteConfirmationId(repair.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currencySymbol,
      navigate,
      permissions,
      filters.sort_by,
      filters.sort_direction,
    ],
  );

  if (isRepairStatsLoading && hasPermission("repair dashboard view")) {
    return (
      <div className="flex h-[100px] w-full items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Repair Dashboard"
          subtitle="Manage repair jobs and track progress"
        />
        <PageHeaderActions>
          {hasPermission("repair job create") && (
            <Button onClick={() => navigate(all_routes.addRepair)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New Repair
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Stats Cards */}
      {hasPermission("repair dashboard view") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {statsCards.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <IconComponent className="h-4 w-4 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <FilterCard>
        <FilterSearch
          placeholder="Search repairs..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value.slice(0, 255))}
          maxLength={255}
        />
        <div className="relative">
          <Button
            variant="outline"
            onClick={() => setFilterModalOpen(true)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge
                variant="default"
                className="ml-1 h-5 w-5 rounded-full p-0 text-[10px] flex items-center justify-center"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </div>
      </FilterCard>

      {/* Active Filter Chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 px-1">
          <span className="text-xs font-medium text-muted-foreground">
            Active Filters:
          </span>

          {filters.status && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              Status:{" "}
              <span className="font-semibold capitalize">
                {filters.status.replace(/_/g, " ")}
              </span>
              <button
                type="button"
                onClick={() => removeFilter("status")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.priority && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              Priority:{" "}
              <span className="font-semibold capitalize">
                {filters.priority}
              </span>
              <button
                type="button"
                onClick={() => removeFilter("priority")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.technician_id && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              Tech:{" "}
              <span className="font-semibold">
                {technicians.find(
                  (t) => t.id.toString() === filters.technician_id.toString(),
                )?.full_name || filters.technician_id}
              </span>
              <button
                type="button"
                onClick={() => removeFilter("technician_id")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.store_id && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              Store:{" "}
              <span className="font-semibold">
                {stores.find(
                  (s) => s.id?.toString() === filters.store_id?.toString(),
                )?.name ||
                  (currentStore?.id?.toString() === filters.store_id?.toString()
                    ? currentStore.name
                    : filters.store_id)}
              </span>
              <button
                type="button"
                onClick={() => removeFilter("store_id")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
                title="Remove store filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {!filters.store_id && currentStore?.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  store_id: currentStore.id.toString(),
                }))
              }
              className="h-7 px-2.5 text-xs border-dashed gap-1 text-muted-foreground hover:text-foreground"
              title="Apply current store filter"
            >
              + Store: {currentStore.name || "Current Store"}
            </Button>
          )}

          {filters.repair_category_ids?.length > 0 &&
            filters.repair_category_ids.map((catId) => {
              const cat = categories.find((c) => Number(c.id) === Number(catId));
              return (
                <Badge
                  key={catId}
                  variant="secondary"
                  className="gap-1 pl-2.5 pr-1 py-1 text-xs"
                >
                  Category:{" "}
                  <span className="font-semibold">{cat?.name || `#${catId}`}</span>
                  <button
                    type="button"
                    onClick={() => removeFilter("repair_category_ids", catId)}
                    className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}

          {filters.date_from && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              From:{" "}
              <span className="font-semibold">
                {new Date(filters.date_from).toLocaleDateString()}
              </span>
              <button
                type="button"
                onClick={() => removeFilter("date_from")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.date_to && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              To:{" "}
              <span className="font-semibold">
                {new Date(filters.date_to).toLocaleDateString()}
              </span>
              <button
                type="button"
                onClick={() => removeFilter("date_to")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {(filters.sort_by !== "created_at" ||
            filters.sort_direction !== "desc") && (
              <Badge
                variant="secondary"
                className="gap-1 pl-2.5 pr-1 py-1 text-xs"
              >
                Sort:{" "}
                <span className="font-semibold capitalize">
                  {filters.sort_by.replace(/_/g, " ")} (
                  {filters.sort_direction.toUpperCase()})
                </span>
                <button
                  type="button"
                  onClick={() => removeFilter("sort")}
                  className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}

          {filters.with && filters.with.length !== DEFAULT_WITH.length && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1 py-1 text-xs"
            >
              Relations:{" "}
              <span className="font-semibold">
                {filters.with.length} included
              </span>
              <button
                type="button"
                onClick={() => removeFilter("with")}
                className="hover:bg-muted rounded-full p-0.5 ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={resetAllFilters}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Table */}
      <TableCard>
        <DataTable
          columns={columns}
          data={repairJobs}
          getRowId={(row) => row.id}
          enableRowSelection={true}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={isRepairJobsLoading}
          onRowClick={(row) => {
            setSelectedRepair(row);
            setViewDetailsModalOpen(true);
          }}
          footerInfo={
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-medium text-foreground">
                Total repairs: {paginationMeta.total}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Per page:</span>
                <Select
                  value={pagination.pageSize.toString()}
                  onValueChange={(val) => {
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: 0,
                      pageSize: Number(val),
                    }));
                  }}
                >
                  <SelectTrigger className="h-8 w-[72px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          }
        />
      </TableCard>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfirmationId}
        onOpenChange={() => setDeleteConfirmationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              repair job.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRepairMutation.mutate(deleteConfirmationId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Repair Details Modal */}
      <RepairDetailsModal
        show={viewDetailsModalOpen}
        onHide={() => setViewDetailsModalOpen(false)}
        viewRepairData={selectedRepair}
        currencySymbol={currencySymbol}
        hasPermission={hasPermission}
        handleEdit={(repair) => {
          setViewDetailsModalOpen(false);
          navigate(all_routes.editRepair.replace(":id", repair.id));
        }}
        onPrintPDF={(repair) => {
          setPdfRepairData(repair);
          setPdfModalOpen(true);
        }}
        onRecordPayment={(repair) => {
          setPaymentRepair(repair);
          setPaymentModalOpen(true);
        }}
      />

      <PDFModal
        show={pdfModalOpen}
        onHide={handleClosePDFModal}
        currentRepairData={pdfRepairData}
        isGeneratingPDF={pdfGenerating}
        handleGeneratePDFReport={handleGeneratePDFReport}
        pdfBlobUrl={pdfBlobUrl}
        onClearPreview={() => {
          if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
          }
        }}
        currencySymbol={currencySymbol}
      />

      <FilterRepairModal
        show={filterModalOpen}
        onHide={() => setFilterModalOpen(false)}
        categories={categories}
        technicians={technicians}
        stores={stores}
        currentStore={currentStore}
        filters={filters}
        setFilters={setFilters}
        pageSize={pagination.pageSize}
        onPageSizeChange={(newSize) =>
          setPagination((prev) => ({
            ...prev,
            pageIndex: 0,
            pageSize: newSize,
          }))
        }
      />

      {/* Status History Modal */}
      <StatusHistoryModal
        show={statusHistoryOpen}
        onHide={() => {
          setStatusHistoryOpen(false);
          setStatusHistoryRepair(null);
        }}
        selectedRepairJob={statusHistoryRepair}
        statusEntries={
          statusHistoryRepair?.status_history ||
          statusHistoryRepair?.statusHistory ||
          []
        }
        handleStatusUpdate={(repair) => {
          setStatusHistoryOpen(false);
          setStatusUpdateRepair(repair);
          setStatusForm({
            status:
              typeof repair.status === "string"
                ? repair.status
                : repair.status?.value || "",
            notes: "",
          });
          setStatusUpdateOpen(true);
        }}
      />

      {/* Add Parts Modal */}
      <AddPartsModal
        show={addPartsOpen}
        onHide={() => {
          setAddPartsOpen(false);
          setAddPartsRepair(null);
          setPartsFormData({
            product_id: "",
            quantity_used: 1,
            unit_cost: 0,
            notes: "",
            serial_numbers: [],
            inventory_batch_id: null,
          });
        }}
        selectedRepairJob={addPartsRepair}
        partsFormData={partsFormData}
        setPartsFormData={setPartsFormData}
        handleProductSelect={handleProductSelect}
        isProductsLoading={isProductsLoading}
        products={products}
        currencySymbol={currencySymbol}
        handleSerialNumbersChange={handleSerialNumbersChange}
        handleSaveParts={handleSaveParts}
        addPartsMutation={addPartsMutation}
        handleViewParts={handleViewParts}
        repairJobs={repairJobs}
      />

      {/* Parts View Modal */}
      <PartsViewModal
        show={partsViewOpen}
        onHide={() => {
          setPartsViewOpen(false);
          setPartsViewRepair(null);
        }}
        selectedRepairJob={partsViewRepair}
        partsEntries={partsViewRepair?.parts || []}
        currencySymbol={currencySymbol}
        repairJobs={repairJobs}
        handleAddParts={handleAddParts}
        products={products}
        hasPermission={hasPermission}
      />

      {/* Labor Entry Modal */}
      <LaborEntriesModal
        show={laborModalOpen}
        onHide={() => {
          setLaborModalOpen(false);
          setLaborRepair(null);
          setNewLaborEntry({
            technician_id: "",
            hourly_rate: "",
            start_time: "",
            end_time: "",
            description: "",
          });
        }}
        selectedRepairJob={laborRepair}
        newLaborEntry={newLaborEntry}
        setNewLaborEntry={setNewLaborEntry}
        handleLaborEntryChange={handleLaborEntryChange}
        isTechniciansLoading={isTechniciansLoading}
        technicians={technicians}
        currencySymbol={currencySymbol}
        handleLaborEntrySave={handleLaborEntrySave}
        addLaborMutation={addLaborMutation}
        handleViewLabor={handleViewLabor}
        repairJobs={repairJobs}
      />

      {/* Labor View Modal */}
      <LaborViewModal
        show={laborViewOpen}
        onHide={() => {
          setLaborViewOpen(false);
          setLaborRepair(null);
        }}
        selectedRepairJob={laborRepair}
        laborEntries={laborRepair?.labor_entries || []}
        currencySymbol={currencySymbol}
        repairJobs={repairJobs}
        handleAddLabor={handleAddLabor}
        hasPermission={hasPermission}
      />

      {/* Status Update Modal */}
      <StatusUpdateModal
        show={statusUpdateOpen}
        onHide={() => {
          setStatusUpdateOpen(false);
          setStatusUpdateRepair(null);
          setStatusForm({ status: "", notes: "" });
        }}
        selectedRepairJob={statusUpdateRepair}
        statusFormData={statusForm}
        setStatusFormData={setStatusForm}
        handleSaveStatus={() =>
          updateStatusMutation.mutate({
            id: statusUpdateRepair.id,
            status: statusForm.status,
            notes: statusForm.notes,
          })
        }
        updateStatusMutation={updateStatusMutation}
        handleViewStatus={(repair) => {
          setStatusHistoryRepair(repair);
          setStatusHistoryOpen(true);
        }}
        repairJobs={repairJobs}
      />

      {/* Record Payment Modal */}
      <RecordRepairPaymentModal
        key={paymentRepair?.id || "repair-payment-modal"}
        show={paymentModalOpen}
        onHide={() => {
          setPaymentModalOpen(false);
          setPaymentRepair(null);
        }}
        repairJob={paymentRepair}
        currencySymbol={currencySymbol}
        hasPermission={hasPermission}
      />
    </PageContainer>
  );
};

export default RepairDashboard;
